import React from 'react'
import { PageShell } from './PageShell'
import { StatCard } from '../components/StatCard'
import { Skeleton, SkeletonTableRows } from '../../../components/ui/Skeleton'
import { ActivityChart } from '../components/ActivityChart'
import type { NudgeChartEvent } from '../components/ActivityChart'
import { NudgeStatusBadge } from '../components/NudgeStatusBadge'
import { RecoveryAlertBanner } from '../components/RecoveryAlertBanner'
import { useDashboard } from '../DashboardContext'
import { daysSince, relativeTimeLabel, initialsFromName } from '../utils'
import { fetchPlans } from '../../../lib/billingApi'
import { formatMoney } from '../../../lib/utils'
import { upgradesEnabled } from '../../../lib/featureFlags'
import { PlanPickerModal } from '../components/PlanPickerModal'
import { readPendingPlan, clearPendingPlan, type PendingPlan } from '../../../lib/pendingPlan'
import type { NudgeQueueSummary, Plan } from '../../../types/api'
import { computeNudgeMetrics, RECOVERED_STATUSES } from '../nudgeMetrics'

export function DashboardOverviewPage({ isActive }: { isActive: boolean }) {
  const { contacts, businessInfo, contactsLoading, queueRows, queueLoading, setActivePage, currentSubscription, reloadSubscription } = useDashboard()

  React.useEffect(() => {
    if (isActive) void reloadSubscription()
  }, [isActive, reloadSubscription])

  // Lookup of the active PRO plan so the upgrade CTA shows the real catalog price.
  const [proPlan, setProPlan] = React.useState<Plan | null>(null)
  const [planPickerOpen, setPlanPickerOpen] = React.useState(false)
  // Carried over from the landing-page pricing CTA — opens the picker pre-set
  // to the plan the visitor originally chose.
  const [pendingPlan, setPendingPlan] = React.useState<PendingPlan | null>(null)
  React.useEffect(() => {
    if (currentSubscription?.plan.code !== 'FREE') return
    let cancelled = false
    fetchPlans()
      .then(plans => { if (!cancelled) setProPlan(plans.find(p => p.code === 'PRO') ?? null) })
      .catch(() => { /* silently ignore — fall back to "Upgrade to Pro" without price */ })
    return () => { cancelled = true }
  }, [currentSubscription?.plan.code])

  // Drain the pending plan choice on first arrival as a FREE user, only if upgrades are live.
  React.useEffect(() => {
    if (!upgradesEnabled) { clearPendingPlan(); return }
    if (currentSubscription?.plan.code !== 'FREE') return
    const stored = readPendingPlan()
    if (!stored) return
    setPendingPlan(stored)
    setPlanPickerOpen(true)
    clearPendingPlan()
  }, [currentSubscription?.plan.code])

  const lapseDays = businessInfo?.lapseWindowDays ?? 60

  // ── Contact-derived stats (lapse eligibility) ──────────────────────────────
  const contactsWithLapse = contacts.map(c => {
    const diffDays = daysSince(c.lastVisitDate)
    return { ...c, diffDays, isLapsed: diffDays >= lapseDays }
  })
  const totalContacts = contacts.length
  const lapsedCount = contactsWithLapse.filter(c => c.isLapsed).length

  // ── Queue-derived stats (single source of truth via nudgeMetrics.ts) ────────
  const metrics = React.useMemo(() => computeNudgeMetrics(queueRows), [queueRows])

  // ── Chart events from real timestamps ─────────────────────────────────────
  const chartEvents: NudgeChartEvent[] = React.useMemo(() => {
    const events: NudgeChartEvent[] = []
    for (const row of queueRows) {
      if (row.sentAt) {
        events.push({ id: `${row.id}-sent`, type: 'sent', at: row.sentAt })
      }
      if (row.replyReceivedAt) {
        events.push({ id: `${row.id}-replied`, type: 'replied', at: row.replyReceivedAt })
      }
      if (RECOVERED_STATUSES.includes(row.status)) {
        // recoveredAt is only in detail; use updatedAt as the recovery timestamp
        events.push({ id: `${row.id}-recovered`, type: 'recovered', at: row.updatedAt })
      }
    }
    return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
  }, [queueRows])

  // ── Recent activity feed ───────────────────────────────────────────────────
  const recentActivityItems = React.useMemo(() => {
    const items: Array<{ id: string; tone: string; text: string; time: string; ts: string }> = []

    for (const row of queueRows) {
      if (row.replyReceivedAt) {
        items.push({
          id: `${row.id}-reply`,
          tone: 'reply',
          text: `${row.contactName} replied to your nudge`,
          time: relativeTimeLabel(row.replyReceivedAt),
          ts: row.replyReceivedAt,
        })
      } else if (row.sentAt) {
        items.push({
          id: `${row.id}-send`,
          tone: 'send',
          text: `Nudge sent to ${row.contactName}`,
          time: relativeTimeLabel(row.sentAt),
          ts: row.sentAt,
        })
      }
    }

    items.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())

    const result = items.slice(0, 4)

    if (lapsedCount > 0) {
      result.push({
        id: 'lapsed-detected',
        tone: 'lapse',
        text: `${lapsedCount} customers currently lapsed`,
        time: `Window: ${lapseDays}+ days`,
        ts: '',
      })
    }
    if (result.length < 2) {
      result.push({
        id: 'daily-scan-status',
        tone: 'lapse',
        text: `Daily scan active at ${businessInfo?.dailyScanTime || '9:00 AM'}`,
        time: 'automation enabled',
        ts: '',
      })
    }
    return result.slice(0, 5)
  }, [queueRows, lapsedCount, lapseDays, businessInfo])

  // ── Lapsed preview table: join contacts with queue status ─────────────────
  const queueByContactId = React.useMemo(() => {
    const map = new Map<string, NudgeQueueSummary>()
    for (const row of queueRows) {
      const existing = map.get(row.contactId)
      if (!existing || new Date(row.createdAt) > new Date(existing.createdAt)) {
        map.set(row.contactId, row)
      }
    }
    return map
  }, [queueRows])

  const lapsedPreviewRows = contactsWithLapse
    .filter(c => c.isLapsed)
    .sort((a, b) => b.diffDays - a.diffDays)
    .slice(0, 4)
    .map(c => ({ ...c, queueRow: queueByContactId.get(c.id) ?? null }))

  const loading = contactsLoading || queueLoading

  return (
    <PageShell id="page-dashboard" isActive={isActive}>
      <div className="content">
        <RecoveryAlertBanner queueRows={queueRows} />
        <div className="stats-row" aria-busy={loading} aria-label="Key metrics">
          <StatCard
            className="sc-orange"
            delay=".05s"
            iconClass="ic-orange"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            }
            label="Lapsed Customers"
            value={loading ? <Skeleton height={30} width={56} style={{ marginBottom: 2 }} /> : String(lapsedCount)}
            delta={
              <>
                <span className="dn">{totalContacts} total contacts</span>
              </>
            }
          />

          <StatCard
            className="sc-blue"
            delay=".1s"
            iconClass="ic-blue"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            }
            label="Nudges Sent"
            value={queueLoading ? <Skeleton height={30} width={56} style={{ marginBottom: 2 }} /> : String(metrics.totalSent)}
            delta={
              metrics.totalFailed > 0 ? (
                <>
                  <span style={{ color: 'var(--red)' }}>{metrics.totalFailed} failed</span>
                  <span className="dn"> · {metrics.awaitingReply} awaiting reply</span>
                </>
              ) : (
                <>
                  <span className="du">{metrics.awaitingReply}</span>
                  <span className="dn"> awaiting reply</span>
                </>
              )
            }
          />

          <StatCard
            className="sc-teal"
            delay=".15s"
            iconClass="ic-teal"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            }
            label="Active Conversations"
            value={queueLoading ? <Skeleton height={30} width={56} style={{ marginBottom: 2 }} /> : String(metrics.activeConvs)}
            delta={
              <>
                <span style={{ color: 'var(--teal)' }}>{metrics.totalReplied}</span>
                <span className="dn"> total replies</span>
              </>
            }
          />

          <StatCard
            className="sc-green"
            delay=".2s"
            iconClass="ic-green"
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 11 12 14 22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
            }
            label="Recovered Customers"
            value={queueLoading ? <Skeleton height={30} width={56} style={{ marginBottom: 2 }} /> : String(metrics.totalRecovered)}
            delta={
              <>
                <span style={{ color: metrics.recoveryRatePct > 0 ? 'var(--green)' : 'var(--text3)' }}>{metrics.recoveryRatePct}%</span>
                <span className="dn"> recovery rate</span>
              </>
            }
          />
        </div>

        <div className="two-col">
          <div className="left-col">
          <div className="panel">
            <div className="panel-header">
              <div>
                <div className="panel-title">Lapsed Customers</div>
                <div className="panel-sub">Haven't visited in {lapseDays}+ days · sorted by urgency</div>
              </div>
              <div className="panel-action" onClick={() => setActivePage('lapsed')}>
                View all →
              </div>
            </div>

            <table className="data-table" style={{ minHeight: 200 }}>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Days lapsed</th>
                  <th>Nudge status</th>
                </tr>
              </thead>
              <tbody>
                {loading && <SkeletonTableRows cols={3} rows={3} />}
                {!loading && lapsedPreviewRows.map(row => {
                  const badgeClass =
                    row.diffDays >= lapseDays + 30
                      ? 'days-critical'
                      : row.diffDays >= lapseDays + 14
                        ? 'days-warn'
                        : 'days-mild'
                  return (
                    <tr key={row.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div
                            className="avatar-circle"
                            style={{ background: 'var(--orange-bg)', color: 'var(--orange)' }}
                          >
                            {initialsFromName(row.fullName)}
                          </div>
                          <div>
                            <div className="cust-name">{row.fullName}</div>
                            <div className="cust-sub">{row.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`days-badge ${badgeClass}`}>{row.diffDays} days</span>
                      </td>
                      <td>
                        {row.queueRow ? (
                          <NudgeStatusBadge status={row.queueRow.status} />
                        ) : (
                          <span className="pill pill-pending">Not queued</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
                {!loading && lapsedPreviewRows.length === 0 ? (
                  <tr>
                    <td colSpan={3}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '28px 16px', color: 'var(--text2)', fontSize: 13 }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.35 }} aria-hidden="true">
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="9 11 12 14 22 4" />
                        </svg>
                        <span>No lapsed customers yet.</span>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>

          </div>

          <div className="mini-stats-panel">
            <div className="mini-stat">
              <div className="mini-stat-label">Awaiting reply</div>
              <div className="mini-stat-val">{metrics.awaitingReply}</div>
              <div className="mini-stat-sub">nudges sent, no reply yet</div>
            </div>
            <div className="mini-stat">
              <div className="mini-stat-label">Recovery rate</div>
              <div className="mini-stat-val" style={{ color: metrics.recoveryRatePct > 0 ? 'var(--accent)' : 'var(--text2)' }}>
                {metrics.recoveryRatePct}
                <span style={{ fontSize: 14 }}>%</span>
              </div>
              <div className="mini-stat-sub">
                {metrics.totalRecovered} of {metrics.funnelSent} contacted
              </div>
            </div>
          </div>
          </div>{/* end left-col */}

          <div className="right-col">
            <div className="panel">
              <div className="panel-header">
                <div>
                  <div className="panel-title">Recent Activity</div>
                  <div className="panel-sub">Live updates</div>
                </div>
              </div>
              <div className="activity-feed">
                {recentActivityItems.length > 0 ? (
                  recentActivityItems.map(item => (
                    <div className="activity-item" key={item.id}>
                      <div
                        className={`act-dot ${
                          item.tone === 'reply'
                            ? 'act-reply'
                            : item.tone === 'send'
                              ? 'act-send'
                              : 'act-lapse'
                        }`}
                      >
                        {item.tone === 'reply' ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="9 17 4 12 9 7" />
                            <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                          </svg>
                        ) : item.tone === 'send' ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13" />
                            <polygon points="22 2 15 22 11 13 2 9 22 2" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                        )}
                      </div>
                      <div className="act-body">
                        <div className="act-text">{item.text}</div>
                        <div className="act-time">{item.time}</div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="activity-item">
                    <div className="act-dot act-signup">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                        <circle cx="8.5" cy="7" r="4" />
                        <line x1="20" y1="8" x2="20" y2="14" />
                        <line x1="23" y1="11" x2="17" y2="11" />
                      </svg>
                    </div>
                    <div className="act-body">
                      <div className="act-text">No recent nudge activity yet</div>
                      <div className="act-time">Waiting for first event</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="panel" style={{ padding: 20 }}>
              {(() => {
                const planName = currentSubscription?.plan.displayName ?? 'Free'
                const planCode = currentSubscription?.plan.code ?? 'FREE'
                const contactLimit = currentSubscription?.plan.contactLimit ?? 50
                const contactLimitLabel = contactLimit < 0 ? '∞' : contactLimit.toLocaleString()
                const contactPct = contactLimit > 0 ? Math.min(100, Math.round((totalContacts / contactLimit) * 100)) : 0
                const isFree = planCode === 'FREE'
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                      <div className="panel-title">{planName} Plan Usage</div>
                      <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", padding: '3px 8px', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text2)', border: '1px solid var(--border)' }}>
                        This month
                      </div>
                    </div>

                    {/* Contacts — limited, show progress bar */}
                    {contactLimit > 0 ? (
                      <div className="usage-bar-wrap" style={{ marginBottom: 14 }}>
                        <div className="usage-label">
                          <span>Contacts</span>
                          <span>
                            <span style={{ color: contactPct >= 80 ? 'var(--orange)' : 'var(--text)' }}>
                              {totalContacts} / {contactLimitLabel}
                            </span>
                            <span style={{ color: 'var(--text3)', marginLeft: 6 }}>{contactPct}%</span>
                          </span>
                        </div>
                        <div className="usage-bar">
                          <div className="usage-fill uf-orange" style={{ width: `${contactPct}%` }} />
                        </div>
                      </div>
                    ) : (
                      /* Unlimited contacts on paid plan — show as stat tile */
                      null
                    )}

                    {/* Nudges sent + Replies tracked — always unlimited, show as stat tiles */}
                    <div style={{ display: 'grid', gridTemplateColumns: contactLimit > 0 ? '1fr 1fr' : '1fr 1fr 1fr', gap: 8, marginBottom: isFree ? 14 : 0 }}>
                      {contactLimit < 0 && (
                        <div className="usage-stat-tile">
                          <div className="usage-stat-label">Contacts</div>
                          <div className="usage-stat-value" style={{ color: 'var(--orange)' }}>{totalContacts.toLocaleString()}</div>
                          <div className="usage-stat-badge">Unlimited</div>
                        </div>
                      )}
                      <div className="usage-stat-tile">
                        <div className="usage-stat-label">Nudges sent</div>
                        <div className="usage-stat-value" style={{ color: 'var(--blue)' }}>{metrics.totalSent.toLocaleString()}</div>
                        <div className="usage-stat-badge">Unlimited</div>
                      </div>
                      <div className="usage-stat-tile">
                        <div className="usage-stat-label">Replies tracked</div>
                        <div className="usage-stat-value" style={{ color: 'var(--accent)' }}>{metrics.totalReplied.toLocaleString()}</div>
                        <div className="usage-stat-badge">Unlimited</div>
                      </div>
                    </div>

                    {isFree && upgradesEnabled && (
                      <button
                        className="upgrade-btn"
                        onClick={() => setPlanPickerOpen(true)}
                      >
                        ✦ Upgrade to Pro{proPlan ? ` — ${formatMoney(proPlan.monthlyCents, proPlan.currency)}/month` : ''}
                      </button>
                    )}
                  </>
                )
              })()}
            </div>
          </div>
        </div>

        <ActivityChart
          allNudgeEvents={chartEvents}
          nudgeSentCount={metrics.totalSent}
        />
      </div>
      {upgradesEnabled && planPickerOpen && (
        <PlanPickerModal
          onClose={() => { setPlanPickerOpen(false); setPendingPlan(null) }}
          initialCycle={pendingPlan?.cycle}
          initialMeteredUnits={pendingPlan?.units}
        />
      )}
    </PageShell>
  )
}
